/**
 * CustButton.tsx — Renders a styled CTA button using MUI `<Button>`.
 *
 * JSON trigger: `uiCompType.method: "renderCustButton"` or via Panel `button` / `buttons` key.
 *
 * **Data fields:**
 *  - `sectionData.title` — Button label text
 *  - `sectionData.link`  — Navigation URL (used as `href`)
 *  - `sectionData.onClick.method` — Global window function to call on click
 *    (e.g. `"openSignUpPopup"`, `"openPopup"`)
 *
 * **Key options:**
 *  - `options.color`   — MUI button colour (`'primary'`, `'secondary'`, …)
 *  - `options.size`    — MUI button size (`'small'`, `'medium'`, `'large'`)
 *  - `options.variant` — MUI button variant (`'contained'`, `'outlined'`, `'text'`)
 *  - `options.newTab`  — Open link in new tab
 *
 * **Tracking:** Calls `trackItem` with `sectionData.trackInfo` on click.
 *
 * **Style slots:** `uiCompType.styles` (applied to the `<Button>` element).
 */
import React from "react";
import { Button } from "@mui/material";
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps } from "./index";
import { trackItem } from "@/utils";

interface CustButtonProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
    index?: number;
}

const getButton = (sectionData: SectionData, index: number) => {
    // ...existing getButton code...
    const color = (sectionData.color as any) || (sectionData.uiCompType?.options?.color as any) || "primary"
    const size = (sectionData.uiCompType?.options?.size as any) || "small";
    const target = sectionData.newTab as any || sectionData.uiCompType?.options?.newTab ? "_blank" : "_self"
    // @ts-ignore
    return (
        <Button
            {...(!sectionData.onClick?.method && { component: "a" })}
            key={sectionData.trackId || ('Button_' + (index))}
            id={sectionData.trackId || ('Button_' + (index))}
            color={color}
            href={sectionData.link}
            {...(!sectionData.onClick?.method && { target: target })}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                trackItem(sectionData?.trackInfo || sectionData?.onClick?.input?.trackInfo || {});
                if (sectionData.onClick?.method) {
                    // @ts-ignore
                    if (typeof window[sectionData.onClick.method] === 'function') {
                        // @ts-ignore
                        window[sectionData.onClick.method](sectionData.onClick.input);
                    } else {
                        console.warn(`Global function ${sectionData.onClick.method} not found.`);
                    }
                }
            }}
            style={{ ...sectionData.uiCompType?.styles }}
            className={cssStyles[sectionData.uiCompType?.className || "custButton"]}
            size={size}
        >
            {sectionData.title}
        </Button>
    );
};

const CustButton = ({ sectionData, props, index = 1 }: CustButtonProps): JSX.Element | null => {
    // ...existing CustButton code...
    const panelClassName = "custButton";
    const button = getButton(sectionData, index);

    return sectionData.uiCompType?.parentStyles ? (
        <div style={{ ...sectionData.uiCompType?.parentStyles }}>
            {button}
        </div>
    ) : (
        <span>
            {button}
        </span>
    );
}
export default CustButton;
